/**
 * Shared server-state hooks.
 *
 * React Query was a dependency and a mounted provider that nothing used: every
 * screen ran its own `useEffect` + `setInterval`, so the dashboard and the
 * activations page each polled the same history endpoint, the intervals kept firing
 * with the tab hidden (720 requests/hour from a forgotten tab), and a failed request
 * left stale data on screen with no error state.
 *
 * The polling rule here is: only poll while something is actually pending, and never
 * while the tab is in the background.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { parseServerDate } from "@/lib/server-time";

export type Activation = {
  id: number;
  service_code: string;
  number: string | null;
  code: string | null;
  status: string;
  cost: number;
  activation_type: string;
  country: string | null;
  hero_id: string | null;
  created_at: string;
  /** Emails only: the site the inbox was bought to verify. */
  target_site?: string | null;
};

export type Service = {
  id: number;
  code: string;
  name: string;
  price_min: number;
  price_available: number;
  count: number;
  count_physical: number;
  is_popular: boolean;
};

export type TicketMessage = {
  id: number;
  ticket_id: number;
  sender_role: string;
  content: string;
  created_at: string;
};

export type Ticket = {
  id: number;
  user_id: number | null;
  /**
   * No account behind this thread — it came from the public contact form.
   *
   * Server-computed rather than derived from `user_id === null` at each call site,
   * because it changes what answering does: a guest gets the whole conversation by
   * email and can reply to it, a registered user gets a notice and reads it in the
   * panel. Whoever is about to type an answer needs to know which.
   */
  is_guest: boolean;
  contact_email: string;
  subject: string;
  status: string;
  created_at: string;
  updated_at: string;
  messages: TicketMessage[];
};

export type ReferralInfo = {
  referral_code: string;
  referral_url: string;
  referred_count: number;
  pending_credits: number;
  /** Spendable now: transferable to the platform balance, or withdrawable. */
  referral_balance: number;
  /** Everything ever earned, including what was already spent or withdrawn. */
  lifetime_credits: number;
  reward_amount: number;
  referral_min_recharge: number;
  min_withdrawal: number;
  referral_enabled: boolean;
  withdrawal_network: string;
  withdrawal_currency: string;
};

export type ReferralWithdrawal = {
  id: number;
  amount: number;
  wallet_address: string;
  network: string;
  currency: string;
  status: "pending" | "paid" | "rejected";
  admin_note: string | null;
  created_at: string | null;
  processed_at: string | null;
};

export const queryKeys = {
  activations: ["activations"] as const,
  services: ["services"] as const,
  tickets: ["tickets"] as const,
  recharges: ["recharges"] as const,
  referral: ["referral"] as const,
  referralWithdrawals: ["referral-withdrawals"] as const,
  accountProducts: ["account-products"] as const,
};

/** How long we keep polling an activation that never resolves. Matches the backend's
 *  20-minute expiry, after which there is nothing left to wait for. */
const ACTIVATION_MAX_AGE_MS = 20 * 60 * 1000;

function hasLiveActivation(activations: Activation[] | undefined): boolean {
  if (!activations?.length) return false;
  const now = Date.now();
  return activations.some(
    (a) => a.status === "pending" && now - parseServerDate(a.created_at) < ACTIVATION_MAX_AGE_MS,
  );
}

/**
 * Activation history, shared by the dashboard, the activations page and the email
 * page — one cache entry instead of three independent polls.
 */
export function useActivations(options?: Partial<UseQueryOptions<Activation[]>>) {
  const { user } = useAuth();
  return useQuery<Activation[]>({
    queryKey: queryKeys.activations,
    queryFn: () => api.get<Activation[]>("/api/users/me/history/activations"),
    enabled: Boolean(user),
    // Poll only while a code could still arrive, and only in the foreground.
    refetchInterval: (query) => (hasLiveActivation(query.state.data) ? 5000 : false),
    refetchIntervalInBackground: false,
    staleTime: 2000,
    ...options,
  });
}

export function useServices() {
  return useQuery<Service[]>({
    queryKey: queryKeys.services,
    queryFn: () => api.get<Service[]>("/api/services"),
    // Prices refresh upstream every 10 minutes; no point re-fetching faster than that.
    staleTime: 5 * 60 * 1000,
  });
}

export function useTickets() {
  const { user } = useAuth();
  return useQuery<Ticket[]>({
    queryKey: queryKeys.tickets,
    queryFn: () => api.get<Ticket[]>("/api/tickets"),
    enabled: Boolean(user),
    // Only keep polling while a ticket is still open and awaiting a reply.
    refetchInterval: (query) =>
      query.state.data?.some((t) => t.status !== "closed") ? 15000 : false,
    refetchIntervalInBackground: false,
  });
}

/** Invalidate everything that a balance-moving action can change. */
export function useRefreshAfterBalanceChange() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.activations }),
      queryClient.invalidateQueries({ queryKey: queryKeys.recharges }),
      refreshUser(),
    ]);
  };
}

export function useCancelActivation() {
  const refresh = useRefreshAfterBalanceChange();
  return useMutation({
    mutationFn: (id: number) => api.post<{ refunded?: number }>(`/api/activations/${id}/cancel`),
    onSuccess: refresh,
  });
}

export function useFinishActivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/api/activations/${id}/finish`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.activations }),
  });
}

export function useDeleteActivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/activations/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.activations }),
  });
}

export function useClearActivationHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete("/api/activations/history/all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.activations }),
  });
}

/**
 * Referral code, wallet and programme settings. Shared by the sidebar widget and the
 * referrals page, so the widget being on every screen costs one request, not one
 * per navigation.
 */
export function useReferralMe() {
  const { user } = useAuth();
  return useQuery<ReferralInfo>({
    queryKey: queryKeys.referral,
    queryFn: () => api.get<ReferralInfo>("/api/referral/me"),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
  });
}

export function useReferralWithdrawals() {
  const { user } = useAuth();
  return useQuery<ReferralWithdrawal[]>({
    queryKey: queryKeys.referralWithdrawals,
    queryFn: () => api.get<ReferralWithdrawal[]>("/api/referral/withdrawals"),
    enabled: Boolean(user),
  });
}

/**
 * Move the whole referral wallet into the platform balance.
 *
 * Sends no body on purpose — the amount is decided by the server from what the
 * wallet holds, so there is nothing here a client could inflate.
 */
export function useTransferReferralBalance() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  return useMutation({
    mutationFn: () =>
      api.post<{ balance: number; referral_balance: number }>("/api/referral/transfer-to-balance"),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.referral }),
        refreshUser(),
      ]);
    },
  });
}

export function useWithdrawReferralBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; wallet_address: string }) =>
      api.post<{ withdrawal: ReferralWithdrawal; referral_balance: number }>(
        "/api/referral/withdraw",
        body,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.referral }),
        queryClient.invalidateQueries({ queryKey: queryKeys.referralWithdrawals }),
      ]);
    },
  });
}

export function useRequestActivation() {
  const refresh = useRefreshAfterBalanceChange();
  return useMutation({
    mutationFn: ({ serviceCode, country }: { serviceCode: string; country?: string }) => {
      const params = new URLSearchParams({ service_code: serviceCode });
      if (country) params.set("country", country);
      return api.post<Activation>(`/api/activations/request?${params}`);
    },
    onSuccess: refresh,
  });
}
